import {
  IConformationBreedResult,
  IConformationGroupResult,
  IConformationNonLicensedBreedResult,
  IConformationShowAwards,
  IJuniorShowmanshipResults,
} from "@eagerdog/interfaces";

import { ValidationError, ValidationResult } from "../types";
import { PLACE_LABELS } from "./labels";

/**
 * Validation for conformation payloads. Web shows these as non-blocking warnings;
 * mobile blocks Save. The API applies the same duplicate rule server-side.
 */

/** Ids that appear more than once in one placement list (blanks ignored). */
export function duplicatePlacementIds(ids: Array<string | undefined>): string[] {
  const seen = new Set<string>();
  const dupes = new Set<string>();
  ids.forEach((id) => {
    if (!id) return;
    if (seen.has(id)) dupes.add(id);
    seen.add(id);
  });
  return Array.from(dupes);
}

/** Slot indexes (0-based) whose dog also appears in an earlier slot. */
export function duplicatePlacementSlots(ids: Array<string | undefined>): number[] {
  const seen = new Set<string>();
  const slots: number[] = [];
  ids.forEach((id, index) => {
    if (!id) return;
    if (seen.has(id)) slots.push(index);
    seen.add(id);
  });
  return slots;
}

export function validatePlacements(
  ids: Array<string | undefined>,
  label: string
): ValidationError[] {
  return duplicatePlacementSlots(ids).map((index) => ({
    field: `${label}.${index}`,
    message: `${label}: the same dog is placed ${PLACE_LABELS[index]} and in an earlier slot.`,
  }));
}

/** An award pick must come from its candidate pool. */
export function validateAwardPool(
  id: string | undefined,
  poolIds: Set<string>,
  label: string
): ValidationError[] {
  if (!id) return [];
  if (poolIds.has(id)) return [];
  return [{ field: label, message: `${label}: the selected dog is not eligible for this award.` }];
}

function result(errors: ValidationError[]): ValidationResult {
  return { valid: errors.length === 0, errors };
}

export function validateBreedPayload(
  payload: IConformationBreedResult,
  pools?: {
    bestMale?: Set<string>;
    bestFemale?: Set<string>;
    bestOfWinners?: Set<string>;
    champion?: Set<string>;
    grandChampion?: Set<string>;
    bestOfBreed?: Set<string>;
  }
): ValidationResult {
  const errors: ValidationError[] = [];
  (payload.class_results || []).forEach((row) => {
    const label = [row.level, row.sex, row.variety].filter(Boolean).join(" ");
    errors.push(...validatePlacements(row.placements, label));
  });
  const awards = payload.awards;
  if (awards && pools) {
    errors.push(...validateAwardPool(awards.best_male, pools.bestMale || new Set(), "Best Male"));
    errors.push(
      ...validateAwardPool(awards.reserve_best_male, pools.bestMale || new Set(), "Reserve Best Male")
    );
    errors.push(
      ...validateAwardPool(awards.best_female, pools.bestFemale || new Set(), "Best Female")
    );
    errors.push(
      ...validateAwardPool(
        awards.reserve_best_female,
        pools.bestFemale || new Set(),
        "Reserve Best Female"
      )
    );
    errors.push(
      ...validateAwardPool(awards.best_of_winners, pools.bestOfWinners || new Set(), "Best of Winners")
    );
    errors.push(...validateAwardPool(awards.champion, pools.champion || new Set(), "Champion"));
    errors.push(
      ...validateAwardPool(awards.reserve_champion, pools.champion || new Set(), "Reserve Champion")
    );
    errors.push(
      ...validateAwardPool(awards.grand_champion, pools.grandChampion || new Set(), "Grand Champion")
    );
    errors.push(
      ...validateAwardPool(
        awards.reserve_grand_champion,
        pools.grandChampion || new Set(),
        "Reserve Grand Champion"
      )
    );
    errors.push(
      ...validateAwardPool(awards.best_of_breed, pools.bestOfBreed || new Set(), "Best of Breed")
    );
  }
  return result(errors);
}

export function validateNonLicensedPayload(
  payload: IConformationNonLicensedBreedResult,
  allIds?: Set<string>
): ValidationResult {
  const errors: ValidationError[] = [];
  if (payload.male_placements) errors.push(...validatePlacements(payload.male_placements, "Male"));
  if (payload.female_placements) {
    errors.push(...validatePlacements(payload.female_placements, "Female"));
  }
  if (payload.placements) errors.push(...validatePlacements(payload.placements, "Placements"));
  if (allIds) {
    errors.push(...validateAwardPool(payload.best_of_breed, allIds, "Best of Breed"));
  }
  return result(errors);
}

export function validateGroupPayload(
  payload: IConformationGroupResult,
  allIds?: Set<string>
): ValidationResult {
  const errors = validatePlacements(payload.placements, "Group");
  if (allIds) {
    payload.placements.forEach((id, index) => {
      errors.push(...validateAwardPool(id, allIds, `Group ${PLACE_LABELS[index] || index + 1}`));
    });
  }
  return result(errors);
}

export function validateShowAwardsPayload(
  payload: IConformationShowAwards,
  poolsByStream?: Partial<Record<"regular" | "altered" | "novice_puppy" | "veteran" | "brace", Set<string>>>
): ValidationResult {
  const errors: ValidationError[] = [];
  (["regular", "altered", "novice_puppy", "veteran", "brace"] as const).forEach((stream) => {
    const pair = payload[stream];
    if (!pair) return;
    errors.push(
      ...validatePlacements([pair.best_in_show, pair.reserve_best_in_show], `${stream} Best in Show`)
    );
    const pool = poolsByStream?.[stream];
    if (pool) {
      errors.push(...validateAwardPool(pair.best_in_show, pool, `${stream} Best in Show`));
      errors.push(
        ...validateAwardPool(pair.reserve_best_in_show, pool, `${stream} Reserve Best in Show`)
      );
    }
  });
  return result(errors);
}

export function validateJuniorPayload(
  payload: IJuniorShowmanshipResults,
  bestJuniorPool?: Set<string>
): ValidationResult {
  const errors: ValidationError[] = [];
  (payload.class_results || []).forEach((row) => {
    errors.push(...validatePlacements(row.placements, row.level));
  });
  if (bestJuniorPool) {
    errors.push(...validateAwardPool(payload.best_junior, bestJuniorPool, "Best Junior Handler"));
  }
  return result(errors);
}
